import { FC, RefObject, useEffect, useImperativeHandle, useMemo } from 'react';
import * as vp from 'vopay';
import { useForm, withStyles, FieldData, BodyText } from 'vopay';
import * as Yup from 'yup';

import { DetailsCard } from 'UI';
import { useBillingFieldRenderer } from '../../utils/renderBillingField';

import styles from './AccountConfiguration.module.scss';

const cn = withStyles(styles);

export type AccountConfigurationHandle = {
  touchAllFields: () => void;
};

export type AccountConfigurationProps = {
  ref?: RefObject<AccountConfigurationHandle | null>;
  data: vp.AccountConfiguration | undefined;
  currency: string;
  // Settings keys that should render optional instead of the default required.
  optionalFields?: string[];
  onValidChange: (isValid: boolean) => void;
  onValuesChange: (values: Record<string, string | boolean>) => void;
};

type FormValues = Record<string, string | boolean>;

const AccountConfiguration: FC<AccountConfigurationProps> = ({
  ref,
  data = {},
  currency,
  optionalFields = [],
  onValidChange,
  onValuesChange,
}) => {
  const renderBillingField = useBillingFieldRenderer(currency);

  const initialValues = useMemo<FormValues>(
    () =>
      Object.entries(data).reduce<FormValues>((acc, [, item]) => {
        acc[item.settings] = item.value ?? (item.type === 'boolean' ? false : '');
        return acc;
      }, {}),
    [],
  );

  const fields = useMemo<FieldData<FormValues>[]>(() => {
    let lastBooleanKey: string | null = null;
    return Object.entries(data).map(([key, item], index) => {
      const controllingBoolean = item.type !== 'boolean' && item.type !== 'select' ? lastBooleanKey : null;
      if (item.type === 'boolean') lastBooleanKey = item.settings;
      return {
        fieldName: item.settings,
        label: () => key,
        visibility: () => item.visible,
        getInputState: () =>
          index === 0
            ? 'disabled'
            : item.type === 'boolean' || optionalFields.includes(item.settings)
              ? 'optional'
              : 'required',
        showField: controllingBoolean ? (values: FormValues) => Boolean(values[controllingBoolean]) : () => true,
        validate: () =>
          item.type === 'boolean'
            ? Yup.boolean()
            : optionalFields.includes(item.settings)
              ? Yup.string()
              : Yup.string().required(),
        renderField: ({ getFormikData }) =>
          renderBillingField(item.type, getFormikData, Object.values(item.options || {})),
        ...(['amount', 'percent', 'numeric'].includes(item.type) && {
          serializeField: (values: FormValues) => vp.sanitizeToNumeric(String(values[item.settings] ?? '')) ?? '',
        }),
      };
    });
  }, [data, renderBillingField, optionalFields]);

  const { ctx, renderFormElements } = useForm<FormValues>({
    config: fields,
    initialValues,
    onSubmit: () => {},
  });

  useEffect(() => {
    onValidChange(ctx.isValid);
  }, [ctx.values, ctx.isValid]);

  useEffect(() => {
    const serialized = Object.fromEntries(
      fields.map((f) => [
        f.fieldName,
        f.serializeField ? (f.serializeField(ctx.values) ?? '') : ctx.values[f.fieldName],
      ]),
    ) as FormValues;
    onValuesChange(serialized);
  }, [ctx.values]);

  useImperativeHandle(
    ref,
    () => ({
      touchAllFields: () => {
        const touched: Record<string, boolean> = {};
        for (const field of fields) touched[field.fieldName] = true;
        void ctx.setTouched(touched);
      },
    }),
    [fields, ctx],
  );

  return (
    <div className={cn('container')}>
      {renderFormElements({
        config: fields,
        override: (fieldData) => (
          <DetailsCard>
            <div key={fieldData.fieldName} className={cn('field')}>
              <BodyText className={cn('label')} weight="md">
                {fieldData.label}
                {fieldData.inputState === 'required' && <span className={cn('asterisk')}>*</span>}
              </BodyText>
              <div className={cn('inputWrapper')}>{fieldData.template}</div>
            </div>
          </DetailsCard>
        ),
      })}
    </div>
  );
};

export default AccountConfiguration;
